import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package, Search, Loader2, ExternalLink, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_STATUSES = ['Confirmed', 'Processing', 'Shipped', 'Out for delivery', 'Delivered', 'Cancelled'];

// Defensive date normalizer — covers Excel serial numbers + dd/mm/yyyy strings
const formatOrderDate = (raw) => {
  if (raw === null || raw === undefined) return "—";
  const s = String(raw).trim();
  if (!s) return "—";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const num = Number(s);
  if (Number.isFinite(num) && num >= 20000 && num <= 80000) {
    const ms = (num * 86400 * 1000) + Date.UTC(1899, 11, 30);
    const d = new Date(ms);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const [, a, b, y] = m;
    return `${y}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
};

export default function Orders() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [days, setDays] = useState("all");
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("all");
  const [region, setRegion] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  // Edit / Delete dialogs (super_admin only)
  const [editing, setEditing] = useState(null);   // { lead_id, order_slot_id, ...draft }
  const [deleting, setDeleting] = useState(null); // { lead_id, order_slot_id, order_lead_id }
  const [saving, setSaving] = useState(false);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (search.trim()) params.append("search", search.trim());
      if (days !== "all") params.append("days", days);
      if (agentId !== "all") params.append("agent_id", agentId);
      if (region !== "all") params.append("region", region);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const r = await axios.get(`${API}/orders?${params.toString()}`);
      setOrders(r.data?.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load orders");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/lead-management/agents`);
        setAgents(r.data?.agents || []);
      } catch (_e) {/* not all roles have access */}
    })();
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, agentId, region, fromDate, toDate]);

  const downloadCsv = () => {
    if (!orders.length) return;
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };    const header = ["Order Date", "Company", "Contact", "Region", "Order Lead ID", "Fabric Type", "Composition", "Quantity", "Order Status", "Invoice Amount", "Phone", "GST", "Assigned Agent"];
    const lines = [header.join(",")];
    orders.forEach(o => {
      lines.push([
        esc(formatOrderDate(o.order_date)),
        esc(o.company_name),
        esc(o.contact_person),
        esc(o.region),
        esc(o.order_lead_id),
        esc(o.fabric_type),
        esc(o.composition),
        esc(o.quantity ?? ""),
        esc(o.order_status),
        esc(o.invoice_amount ?? ""),
        esc(o.phone),
        esc(o.gst_number),
        esc(o.agent_name),
      ].join(","));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openEdit = (o) => {
    setEditing({
      lead_id: o.lead_id,
      order_slot_id: o.order_slot_id,
      order_lead_id: o.order_lead_id || "",
      fabric_type: o.fabric_type || "",
      composition: o.composition || "",
      order_date: o.order_date || "",
      quantity: o.quantity ?? "",
      order_status: o.order_status || "",
      invoice_amount: o.invoice_amount ?? "",
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        order_lead_id: editing.order_lead_id,
        fabric_type: editing.fabric_type,
        composition: editing.composition,
        order_date: editing.order_date,
        quantity: editing.quantity === "" ? null : editing.quantity,
        order_status: editing.order_status,
        invoice_amount: editing.invoice_amount === "" ? null : editing.invoice_amount,
      };
      const { data } = await axios.patch(
        `${API}/orders/${editing.lead_id}/${editing.order_slot_id}`,
        payload,
      );
      const changedCount = Object.keys(data.changes || {}).length;
      toast.success(changedCount ? `Order updated (${changedCount} field${changedCount !== 1 ? "s" : ""})` : "Order saved — no changes");
      setEditing(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/orders/${deleting.lead_id}/${deleting.order_slot_id}`);
      toast.success(`Order ${deleting.order_lead_id} deleted`);
      setDeleting(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="orders-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-emerald-600" /> Orders
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Every order captured against a lead — includes historical / imported orders.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!orders.length} data-testid="orders-export-btn">
          Export CSV
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search company, order id, fabric, composition, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchOrders()}
                className="pl-9"
                data-testid="orders-search"
              />
            </div>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="orders-days"><SelectValue placeholder="Time range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger data-testid="orders-region"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All regions</SelectItem>
                <SelectItem value="India">India</SelectItem>
                <SelectItem value="Bangladesh">Bangladesh</SelectItem>
              </SelectContent>
            </Select>
            {agents.length > 0 && (
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger data-testid="orders-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2">
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} placeholder="From" />
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} placeholder="To" />
            </div>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setSearch(""); setDays("all"); setAgentId("all"); setRegion("all"); setFromDate(""); setToDate(""); }}>Clear</Button>
            <Button onClick={fetchOrders}><Search className="w-4 h-4 mr-2" />Search</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">
            {orders.length} order{orders.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="py-12 text-center text-slate-400">No orders found for the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Order Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Company</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Contact</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Region</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Order Lead ID</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Fabric Type</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Composition</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Invoice ₹</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Phone / GST</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Assigned Agent</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {orders.map((o, idx) => (
                    <tr key={`${o.lead_id}-${o.order_slot_id}-${idx}`} className="hover:bg-slate-50" data-testid={`order-row-${idx}`}>
                      <td className="py-2 px-3 text-slate-700">{formatOrderDate(o.order_date)}</td>
                      <td className="py-2 px-3 font-medium text-slate-900">{o.company_name || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{o.contact_person || "—"}</td>
                      <td className="py-2 px-3 text-slate-500 text-xs">{o.region || "—"}</td>
                      <td className="py-2 px-3 font-mono text-xs text-emerald-700">{o.order_lead_id || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{o.fabric_type || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{o.composition || "—"}</td>
                      <td className="py-2 px-3 text-right text-slate-700 tabular-nums">{o.quantity != null ? Number(o.quantity).toLocaleString() : "—"}</td>
                      <td className="py-2 px-3">
                        {o.order_status ? (
                          <span className={`inline-block text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded ${
                            o.order_status === 'Delivered' ? 'bg-emerald-100 text-emerald-700'
                            : o.order_status === 'Cancelled' ? 'bg-rose-100 text-rose-700'
                            : o.order_status === 'Shipped' || o.order_status === 'Out for delivery' ? 'bg-blue-100 text-blue-700'
                            : o.order_status === 'Processing' ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-700'
                          }`}>{o.order_status}</span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="py-2 px-3 text-right text-slate-700 tabular-nums">{o.invoice_amount != null ? Number(o.invoice_amount).toLocaleString() : "—"}</td>
                      <td className="py-2 px-3 text-slate-500 text-xs">{o.phone || o.gst_number || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{o.agent_name || "—"}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link to={`/leads/${o.lead_id}`} className="text-blue-600 hover:text-blue-700" title="Open lead" data-testid={`order-open-lead-${idx}`}>
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                          {isSuperAdmin && (
                            <>
                              <button
                                type="button"
                                onClick={() => openEdit(o)}
                                className="text-slate-500 hover:text-blue-600"
                                title="Edit order"
                                data-testid={`order-edit-btn-${idx}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleting({ lead_id: o.lead_id, order_slot_id: o.order_slot_id, order_lead_id: o.order_lead_id })}
                                className="text-slate-500 hover:text-rose-600"
                                title="Delete order"
                                data-testid={`order-delete-btn-${idx}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Super-admin: Edit order dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="order-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit order</DialogTitle>
            <DialogDescription>
              Super-admin only. Every change is logged to the audit trail.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label htmlFor="order_lead_id" className="text-xs">Order Lead ID</Label>
                <Input id="order_lead_id" value={editing.order_lead_id}
                  onChange={(e) => setEditing({ ...editing, order_lead_id: e.target.value })}
                  data-testid="edit-order-lead-id" />
              </div>
              <div>
                <Label htmlFor="fabric_type" className="text-xs">Fabric Type</Label>
                <Input id="fabric_type" value={editing.fabric_type}
                  onChange={(e) => setEditing({ ...editing, fabric_type: e.target.value })}
                  data-testid="edit-fabric-type" />
              </div>
              <div>
                <Label htmlFor="composition" className="text-xs">Composition</Label>
                <Input id="composition" value={editing.composition}
                  onChange={(e) => setEditing({ ...editing, composition: e.target.value })}
                  data-testid="edit-composition" />
              </div>
              <div>
                <Label htmlFor="order_date" className="text-xs">Order Date (YYYY-MM-DD)</Label>
                <Input id="order_date" type="date" value={editing.order_date}
                  onChange={(e) => setEditing({ ...editing, order_date: e.target.value })}
                  data-testid="edit-order-date" />
              </div>
              <div>
                <Label htmlFor="quantity" className="text-xs">Quantity</Label>
                <Input id="quantity" type="text" value={editing.quantity}
                  onChange={(e) => setEditing({ ...editing, quantity: e.target.value })}
                  placeholder="e.g. 1250"
                  data-testid="edit-quantity" />
              </div>
              <div>
                <Label htmlFor="order_status" className="text-xs">Order Status</Label>
                <Select value={editing.order_status || "__none__"} onValueChange={(v) => setEditing({ ...editing, order_status: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-order-status"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {ORDER_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label htmlFor="invoice_amount" className="text-xs">Invoice Amount</Label>
                <Input id="invoice_amount" type="text" value={editing.invoice_amount}
                  onChange={(e) => setEditing({ ...editing, invoice_amount: e.target.value })}
                  placeholder="e.g. 187500 or ₹1,87,500"
                  data-testid="edit-invoice-amount" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super-admin: Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="order-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <strong className="font-mono text-slate-800">{deleting?.order_lead_id}</strong> from the lead's order history. The action is logged to the audit trail. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
